import type { ReactElement, ReactNode } from 'react'
import { DataTable } from 'mantine-datatable'
import { type EventOutcomeData, capitalize } from '@terros/common'
import { Link, useNavigate, createFileRoute } from '@tanstack/react-router'
import { Group } from '@mantine/core'
import { sortEventOutcomes, useEventOutcomes } from '@front/model'
import { ButtonAdd, NotReady, OutcomeColor, Page } from '@components'

function EventOutcomes(): ReactElement {
  const { data, isLoading, error, refetch, isFetching } = useEventOutcomes()
  const navigate = useNavigate()
  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Outcomes' isLoading={isLoading} />

  const sorted = sortEventOutcomes(data.outcomes)

  const edit = ({ record }: { record: EventOutcomeData }) => {
    navigate({ to: record.outcomeId })
  }

  const canEdit = !data.includesDefaults

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title='Closer Outcomes'>
        <Group>{canEdit && <ButtonAdd component={Link} to='add' />}</Group>
      </Page.Heading>
      <Page.Content>
        <DataTable
          highlightOnHover
          striped
          idAccessor='outcomeId'
          records={sorted}
          columns={[
            {
              accessor: 'color',
              render: OutcomeColor,
            },
            { accessor: 'name' },
            {
              accessor: 'occurred',
              render: (r) => <Boolean value={r.occurred} />,
            },
            {
              accessor: 'reschedule',
              render: (r) => <Boolean value={r.reschedule} />,
            },
            {
              accessor: 'closed',
              render: (r) => <Boolean value={r.closed} />,
            },
            {
              accessor: 'show',
              render: Show,
            },
            {
              accessor: 'fault',
              render: Fault,
            },
          ]}
          onRowClick={canEdit ? edit : undefined}
        />
      </Page.Content>
    </Page>
  )
}

const Fault = (record: EventOutcomeData): ReactNode => {
  if (record.fault) return capitalize(record.fault)
}

const Show = (record: EventOutcomeData): ReactNode => {
  if (record.show === 'before') return 'Before Event'
  if (record.show === 'after') return 'After Event'
  return 'Always'
}

const Boolean = ({ value }: { value?: boolean }): ReactNode => {
  if (value) return 'Yes'
  if (value === false) return 'No'
}

export const Route = createFileRoute('/settings/outcome/')({
  component: EventOutcomes,
})
